// F-113 à F-116 : demandes facultatives de mise en relation GLI / PNO, accord explicite, transmission et suivi.
import { useState } from "react";
import { post } from "@core/api";
import { dateTime, money } from "@core/format";
import { useGet, useInvalidate } from "@core/hooks";
import { useOrg } from "@core/org";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, KeyValues, Loading, Modal, Notice, PageHeader, Select, Table, Textarea, fieldError, useToast } from "@core/ui";
import { LEAD_STATUS, Mode, PRODUCT_LABEL, useAction } from "./shared";

type Partner = { id: string; code: string; name: string; kind: string; products: string[]; registration: string; description: string; channels: string[]; simulated: boolean };
const CHANNELS: Record<string, string> = { email: "Courriel", telephone: "Téléphone", sms: "SMS" };

export function InsurancePage() {
  const org = useOrg();
  const partners = useGet<any>(org.api("/insurance/partners"));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Espace"
        title="Partenaires assurance"
        intro="Demandez, si vous le souhaitez, à être contacté par un assureur ou un courtier pour une assurance loyers impayés (GLI) ou propriétaire non occupant (PNO). Refuser ne change rien à votre usage de Bailivo."
        actions={partners.data ? <Mode mode={partners.data.gateway.mode} /> : null}
      />
      {partners.data ? (
        <Notice tone="simulateur" title="Partenaires fictifs">
          <ul className="list-plain">
            {(partners.data.notes as string[]).map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </Notice>
      ) : null}
      {org.can("operate") ? (
        <Card title="Nouvelle demande de contact">
          <LeadForm />
        </Card>
      ) : null}
      <LeadsList />
    </div>
  );
}

/** Formulaire de demande : choix explicite du produit, du partenaire, des canaux ; aperçu exact des données ; accord non coché. */
export function LeadForm({ campaignId, propertyId, product: fixedProduct }: { campaignId?: string; propertyId?: string; product?: "gli" | "pno" }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const partners = useGet<any>(org.api("/insurance/partners"));
  const targets = useGet<any>(campaignId || propertyId ? null : org.api("/insurance/targets"));
  const [product, setProduct] = useState<"gli" | "pno">(fixedProduct ?? "gli");
  const [partnerId, setPartnerId] = useState("");
  const [target, setTarget] = useState("");
  const [channels, setChannels] = useState<string[]>(["email"]);
  const [preview, setPreview] = useState<any>(null);
  const [accepted, setAccepted] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const action = useAction();
  const list: Partner[] = (partners.data?.items ?? []).filter((p: Partner) => p.products.includes(product));
  const body = () => {
    const [kind, id] = target.split(":");
    return {
      product,
      partner_id: partnerId,
      channels,
      campaign_id: campaignId ?? (kind === "campagne" ? id : undefined),
      property_id: propertyId ?? (kind === "logement" ? id : undefined),
    };
  };
  const reset = () => {
    setPreview(null);
    setAccepted(false);
  };
  const doPreview = async () => {
    const r = await action.run(() => post<any>(org.api("/insurance/preview"), body()));
    if (r) {
      setPreview(r);
      setAccepted(false);
    }
  };
  const create = async () => {
    const r = await action.run(() => post<any>(org.api("/insurance/leads"), { ...body(), accepted, consent_version: preview?.consent_version }));
    if (r) {
      setCreated(r.id);
      reset();
      invalidate(org.api("/insurance/leads"));
      toast("Accord enregistré. Vérifiez puis transmettez la demande.");
    }
  };
  const transmit = async () => {
    if (!created) return;
    const r = await action.run(() => post<any>(org.api(`/insurance/leads/${created}/transmit`)));
    invalidate(org.api("/insurance/leads"));
    if (r) {
      toast(`Demande transmise, référence ${r.partner_ref}.`);
      setCreated(null);
    }
  };
  if (partners.isLoading) return <Loading />;
  if (!partners.data?.items?.length) return <Empty title="Aucun partenaire configuré.">Le choix des partenaires reste à arbitrer (D-27).</Empty>;
  if (created)
    return (
      <div className="stack-sm">
        <Notice tone="succes">Votre accord est enregistré. Rien n’a encore été envoyé : vous pouvez transmettre maintenant ou retirer la demande dans la liste.</Notice>
        <ErrorBox error={action.error} />
        <div className="row">
          <Button busy={action.busy} onClick={transmit}>
            Transmettre au partenaire
          </Button>
          <Button variant="discret" onClick={() => setCreated(null)}>
            Plus tard
          </Button>
        </div>
      </div>
    );
  return (
    <div className="stack-sm">
      <ErrorBox error={action.error && !fieldError(action.error, "accepted") ? action.error : null} />
      <div className="form-row">
        {fixedProduct ? null : (
          <Select
            label="Type de demande"
            value={product}
            options={[
              { value: "gli", label: PRODUCT_LABEL.gli },
              { value: "pno", label: PRODUCT_LABEL.pno },
            ]}
            onChange={(e) => {
              setProduct(e.target.value as "gli" | "pno");
              setPartnerId("");
              reset();
            }}
          />
        )}
        <Select
          label="Partenaire"
          value={partnerId}
          error={fieldError(action.error, "partner_id")}
          options={[{ value: "", label: "Choisissez un partenaire" }, ...list.map((p) => ({ value: p.id, label: `${p.name}${p.simulated ? " (simulé)" : ""}` }))]}
          onChange={(e) => {
            setPartnerId(e.target.value);
            reset();
          }}
        />
        {!campaignId && !propertyId ? (
          <Select
            label="Concerne"
            value={target}
            options={[
              { value: "", label: "Aucun bien en particulier" },
              ...(targets.data?.campaigns ?? []).map((c: any) => ({ value: `campagne:${c.id}`, label: `Campagne : ${c.title}` })),
              ...(targets.data?.properties ?? []).map((p: any) => ({ value: `logement:${p.id}`, label: `Bien : ${p.label}` })),
            ]}
            onChange={(e) => {
              setTarget(e.target.value);
              reset();
            }}
          />
        ) : null}
      </div>
      {partnerId ? (
        <fieldset className="stack-sm">
          <legend className="small">Le partenaire peut me contacter par</legend>
          {(list.find((p) => p.id === partnerId)?.channels ?? []).map((c) => (
            <Checkbox
              key={c}
              label={CHANNELS[c] ?? c}
              checked={channels.includes(c)}
              onChange={(v) => {
                setChannels(v ? [...channels, c] : channels.filter((x) => x !== c));
                reset();
              }}
            />
          ))}
          {fieldError(action.error, "channels") ? <p className="bv-champ-aide" role="alert">{fieldError(action.error, "channels")}</p> : null}
        </fieldset>
      ) : null}
      {!preview ? (
        <p>
          <Button variant="secondaire" disabled={!partnerId} busy={action.busy} onClick={doPreview}>
            Voir les données qui seraient transmises
          </Button>
        </p>
      ) : (
        <div className="plateforme-accord">
          <strong>Données transmises à {preview.partner.name}</strong>
          <KeyValues
            items={[
              ["Vous", [preview.payload.demandeur.nom, preview.payload.demandeur.email, preview.payload.demandeur.telephone].filter(Boolean).join(" · ")],
              ["Bien", preview.payload.bien ? [preview.payload.bien.type, preview.payload.bien.code_postal, preview.payload.bien.commune, preview.payload.bien.surface_m2 ? `${preview.payload.bien.surface_m2} m²` : null, preview.payload.bien.meuble ? "meublé" : "vide"].filter(Boolean).join(" · ") : "Aucun bien précisé"],
              ...(preview.payload.location ? [["Loyer", `${money(preview.payload.location.loyer_mensuel_cents)} + ${money(preview.payload.location.charges_mensuelles_cents)} de charges`] as [string, string]] : []),
              ["Canaux", (preview.payload.canaux as string[]).map((c) => CHANNELS[c] ?? c).join(", ")],
            ]}
          />
          <p className="small">Aucune donnée ni pièce de vos candidats, locataires ou garants n’est transmise.</p>
          <p className="small muted">
            Partenaire : {preview.partner.name}, {preview.partner.kind}. {preview.partner.registration}
          </p>
          <Checkbox label={preview.consent_text} checked={accepted} onChange={setAccepted} />
          {fieldError(action.error, "accepted") ? (
            <p className="bv-champ-aide" role="alert">
              {fieldError(action.error, "accepted")}
            </p>
          ) : null}
          <div className="row">
            <Button busy={action.busy} onClick={create}>
              Enregistrer ma demande
            </Button>
            <Button variant="discret" onClick={reset}>
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function LeadsList({ filter }: { filter?: (l: any) => boolean }) {
  const org = useOrg();
  const q = useGet<any>(org.api("/insurance/leads"));
  const [open, setOpen] = useState<string | null>(null);
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const items = (q.data.items as any[]).filter(filter ?? (() => true));
  return (
    <Card title="Demandes">
      {items.length === 0 ? (
        <Empty title="Aucune demande de mise en relation." />
      ) : (
        <Table caption="Demandes de mise en relation">
          <thead>
            <tr>
              <th>Date</th>
              <th>Demande</th>
              <th>Partenaire</th>
              <th>Statut</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id}>
                <td>{dateTime(l.created_at)}</td>
                <td>
                  {PRODUCT_LABEL[l.product] ?? l.product}
                  {l.property_label ? <span className="block small muted">{l.property_label}</span> : null}
                </td>
                <td>
                  {l.partner_name} {l.partner_simulated ? <Mode mode="simulator" /> : null}
                </td>
                <td>
                  <Badge tone={LEAD_STATUS[l.status]?.tone ?? "neutre"}>{LEAD_STATUS[l.status]?.label ?? l.status}</Badge>
                </td>
                <td className="right">
                  <Button variant="discret" small onClick={() => setOpen(l.id)}>
                    Détail
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {open ? <LeadDetail id={open} onClose={() => setOpen(null)} /> : null}
    </Card>
  );
}

function LeadDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const org = useOrg();
  const toast = useToast();
  const invalidate = useInvalidate();
  const q = useGet<any>(org.api(`/insurance/leads/${id}`));
  const action = useAction();
  const [note, setNote] = useState("");
  const refresh = () => invalidate(org.api("/insurance/leads"));
  const act = async (path: string, body: unknown, message: (r: any) => string) => {
    const r = await action.run(() => post<any>(org.api(`/insurance/leads/${id}/${path}`), body));
    refresh();
    if (r) toast(message(r));
  };
  const l = q.data?.lead;
  const can = org.can("operate");
  return (
    <Modal open onClose={onClose} title="Demande de mise en relation">
      {q.isLoading ? <Loading /> : null}
      {l ? (
        <div className="stack-sm">
          <ErrorBox error={action.error} />
          <KeyValues
            items={[
              ["Demande", PRODUCT_LABEL[l.product] ?? l.product],
              ["Partenaire", l.partner_name],
              ["Statut", LEAD_STATUS[l.status]?.label ?? l.status],
              ["Référence partenaire", l.partner_ref ?? "—"],
              ["Tentatives d’envoi", l.attempts],
              ["Accord", l.consent_withdrawn_at ? `Retiré le ${dateTime(l.consent_withdrawn_at)}` : `Donné le ${dateTime(q.data.consent.given_at)} (version ${q.data.consent.version})`],
            ]}
          />
          {l.last_error ? <Notice tone="erreur">{l.last_error}</Notice> : null}
          <details>
            <summary className="small">Données transmises</summary>
            <pre className="plateforme-code">{JSON.stringify(l.payload, null, 2)}</pre>
          </details>
          <details>
            <summary className="small">Texte de l’accord présenté</summary>
            <p className="small">{q.data.consent.text}</p>
          </details>
          <strong>Historique</strong>
          <ul className="list-plain small">
            {q.data.events.map((e: any, i: number) => (
              <li key={i}>
                {dateTime(e.created_at)} · {LEAD_STATUS[e.status_to]?.label ?? e.status_to} · {e.origin === "partenaire" ? "retour du partenaire" : e.origin === "bailleur" ? "vous" : "Bailivo"} · {e.note}
              </li>
            ))}
          </ul>
          {can ? (
            <div className="stack-sm">
              <div className="row">
                {l.status === "autorise" || l.status === "echec_transmission" ? (
                  <Button busy={action.busy} onClick={() => act("transmit", {}, (r) => `Demande transmise, référence ${r.partner_ref}.`)}>
                    {l.status === "echec_transmission" ? "Réessayer la transmission" : "Transmettre au partenaire"}
                  </Button>
                ) : null}
                {l.status !== "retire" && l.status !== "sans_suite" && !l.consent_withdrawn_at ? (
                  <Button variant="danger" busy={action.busy} onClick={() => act("withdraw", {}, (r) => (r.transmitted ? "Accord retiré ; demande d’effacement adressée au partenaire." : "Demande retirée ; rien n’a été envoyé."))}>
                    Retirer mon accord
                  </Button>
                ) : null}
              </div>
              {["transmis", "recu", "contacte", "devis_propose"].includes(l.status) ? (
                <div className="stack-sm">
                  <Textarea label="Note (facultative)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                  <div className="row">
                    <Button variant="secondaire" small busy={action.busy} onClick={() => act("status", { status: "contacte", note }, () => "Statut mis à jour.")}>
                      J’ai été contacté
                    </Button>
                    <Button variant="secondaire" small busy={action.busy} onClick={() => act("status", { status: "sans_suite", note }, () => "Demande close sans suite.")}>
                      Clore sans suite
                    </Button>
                  </div>
                  <p className="small muted">La souscription n’est enregistrée que sur confirmation du partenaire, et ne signifie pas que la couverture a pris effet.</p>
                </div>
              ) : null}
              {l.partner_simulated && l.partner_ref && l.status !== "retire" ? (
                <div className="row">
                  <span className="small muted">Simuler un retour du partenaire :</span>
                  {["recu", "contacte", "devis_propose", "souscrit", "sans_suite"].map((s) => (
                    <Button key={s} variant="discret" small busy={action.busy} onClick={() => act("simulate", { status: s }, (r) => r.resultat)}>
                      {LEAD_STATUS[s].label}
                    </Button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

/** Onglet d'une campagne : demande GLI facultative. */
export function CampaignInsuranceTab({ campaignId }: { orgId: string; campaignId: string }) {
  const org = useOrg();
  return (
    <div className="stack">
      <Notice>Demande facultative et distincte du contrôle des dossiers : elle ne souscrit aucune assurance et ne transmet aucune donnée de vos candidats.</Notice>
      {org.can("operate") ? (
        <Card title="Être contacté pour une assurance loyers impayés">
          <LeadForm campaignId={campaignId} product="gli" />
        </Card>
      ) : null}
      <LeadsList filter={(l) => l.campaign_id === campaignId} />
    </div>
  );
}

/** Onglet d'un logement : demande PNO (ou GLI) facultative. */
export function PropertyInsuranceTab({ propertyId }: { orgId: string; propertyId: string }) {
  const org = useOrg();
  return (
    <div className="stack">
      <Notice>Demande facultative : un contact pour une assurance propriétaire non occupant ne déclenche aucune demande GLI, et inversement.</Notice>
      {org.can("operate") ? (
        <Card title="Être contacté par un assureur">
          <LeadForm propertyId={propertyId} />
        </Card>
      ) : null}
      <LeadsList filter={(l) => l.property_id === propertyId} />
    </div>
  );
}
